import React from "react";
import { useMemo, useState } from 'react';
import { useAdminToast } from '../shell/AdminToastContext';
import { useVencimientos } from '../Vencimientos/hooks';
import { fmtMoney } from '../Vencimientos/vencimientosConfig';
import KpiCards from '../Vencimientos/KpiCards';
import FiltersBar from '../Vencimientos/FiltersBar';
import FeedTable from '../Vencimientos/FeedTable';
import ClienteDrawer from '../Vencimientos/ClienteDrawer';
import NuevoClienteWizard from '../Vencimientos/NuevoClienteWizard';
import { vencimientosApi } from '../Vencimientos/api';
import '../Vencimientos/vencimientos.css';

// Mapea cada KPI clickeable a su quick-filter sobre el backend.
const KPI_FILTER = {
  vencidos: { estado: 'vencido' },
  por_vencer: { estado: 'por_vencer' },
  monto_mes: { estado: '' }, // el mes (YYYY-MM actual) se aplica en apiFiltros
  clientes: { estado: '' },
};

const PER_PAGE = 25;

// El filtro Mes viene como 1-12 (o ''); el backend espera 'YYYY-MM'.
function toBackendMes(mes) {
  if (!mes) return '';
  const s = String(mes);
  if (/^\d{4}-\d{2}$/.test(s)) return s; // ya viene como YYYY-MM
  if (/^\d{1,2}$/.test(s)) return `${new Date().getFullYear()}-${s.padStart(2, '0')}`;
  return '';
}

const EMPTY = {
  tipo: 'todos',
  estado: 'todos',
  mes: '',
  search: '',
  aseguradora: 'todos',
  solo_proximos: '',
};

export const VencimientosPage = () => {
  const toast = useAdminToast();

  const [filtros, setFiltros] = useState(EMPTY);
  const [kpiActive, setKpiActive] = useState(null);
  const [page, setPage] = useState(1);
  const [drawerUser, setDrawerUser] = useState(null);
  const [focusUid, setFocusUid] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [busyUid, setBusyUid] = useState(null);

  // Filtros efectivos que se mandan al backend (incluye overrides del KPI activo).
  const apiFiltros = useMemo(() => {
    const base = { ...filtros, page, per_page: PER_PAGE };
    if (kpiActive && KPI_FILTER[kpiActive]) {
      Object.assign(base, KPI_FILTER[kpiActive]);
    }
    // El KPI "Monto del mes" filtra por el mes actual; si no, convierte el mes
    // del filtro (1-12) a 'YYYY-MM' que es lo que entiende el backend.
    if (kpiActive === 'monto_mes') {
      const now = new Date();
      base.mes = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    } else {
      base.mes = toBackendMes(base.mes);
    }
    return base;
  }, [filtros, kpiActive, page]);

  const { data, kpis, aseguradoras, pagination, loading, refresh } = useVencimientos(apiFiltros);

  const hasActiveFilters = useMemo(() => {
    return (
      filtros.tipo !== 'todos' ||
      filtros.estado !== 'todos' ||
      filtros.mes !== '' ||
      filtros.search !== '' ||
      filtros.aseguradora !== 'todos' ||
      !!kpiActive
    );
  }, [filtros, kpiActive]);

  function handleFilterChange(key, value) {
    setKpiActive(null); // un cambio manual de filtro cancela el quick-filter del KPI
    setPage(1);
    setFiltros((f) => {
      const next = { ...f, [key]: value };
      // Al salir de Seguros la aseguradora se oculta: limpia su valor para no
      // dejar el feed vacio (el backend descarta placas si hay aseguradora).
      if (key === 'tipo' && value !== 'todos' && value !== 'seguro') {
        next.aseguradora = 'todos';
      }
      return next;
    });
  }

  function handleSearch(value) {
    setPage(1);
    setFiltros((f) => ({ ...f, search: value }));
  }

  function handleReset() {
    setFiltros(EMPTY);
    setKpiActive(null);
    setPage(1);
  }

  function handleKpiPick(id) {
    setPage(1);
    setKpiActive((cur) => (cur === id ? null : id));
  }

  function openDrawer(userId, uid) {
    if (!userId) return;
    setDrawerUser(userId);
    setFocusUid(uid || null);
  }

  function closeDrawer() {
    setDrawerUser(null);
    setFocusUid(null);
    refresh(); // al volver al dashboard, refresca el feed (refleja pagos hechos en el drawer)
  }

  // Accion rapida desde la fila del feed: hace el toggle correcto segun el tipo
  // (seguro->marcarPago, placa->renovarPlaca, tenencia/renta->marcarObligacion).
  // No abre el drawer; refresca el feed al terminar. busyUid bloquea solo esa fila.
  async function handleQuickAction(row, paid) {
    const key = row.uid || row.id;
    setBusyUid(key);
    try {
      if (row.tipo === 'placa') {
        await vencimientosApi.renovarPlaca(row.id, paid);
      } else if (row.tipo === 'tenencia' || row.tipo === 'renta') {
        await vencimientosApi.marcarObligacion(row.id, paid);
      } else {
        await vencimientosApi.marcarPago(row.id, paid);
      }
      const summary = paid
        ? (row.tipo === 'placa' ? 'Placa marcada como renovada' : 'Pago registrado')
        : (row.tipo === 'placa' ? 'Renovacion revertida' : 'Pago revertido');
      toast?.current?.show?.({ severity: 'success', summary, life: 2200 });
      refresh();
    } catch (e) {
      toast?.current?.show?.({ severity: 'error', summary: 'No se pudo actualizar', life: 3000 });
    } finally {
      setBusyUid(null);
    }
  }

  const total = pagination?.total ?? data.length;
  const totalMonto = useMemo(
    () => data.reduce((s, r) => s + (Number(r.monto) || 0), 0),
    [data]
  );

  return (
    <div className="venc">
      {/* Hero ligero */}
      <div className="venc-hero">
        <div className="left">
          <div className="eyebrow">Cartera · Vencimientos</div>
          <h1>Vencimientos de <b>cartera</b></h1>
          <div className="pipeline">
            <b>{total}</b> vencimientos · <b>{fmtMoney(totalMonto)}</b> en pantalla
          </div>
        </div>
        <div className="right">
          <button type="button" className="venc-btn primary" onClick={() => setWizardOpen(true)}>
            <i className="pi pi-plus" />Nuevo cliente externo
          </button>
        </div>
      </div>

      {/* KPIs clickeables */}
      <KpiCards kpis={kpis} active={kpiActive} onPick={handleKpiPick} />

      {/* Filtros in-page */}
      <FiltersBar
        tipo={filtros.tipo}
        estado={filtros.estado}
        mes={filtros.mes}
        search={filtros.search}
        aseguradora={filtros.aseguradora}
        aseguradoras={aseguradoras}
        hasActiveFilters={hasActiveFilters}
        onChange={handleFilterChange}
        onSearch={handleSearch}
        onReset={handleReset}
      />

      {/* Banner de revision */}
      {data.some((r) => r.needs_review) && (
        <div className="venc-review-banner">
          <i className="pi pi-flag-fill" />
          <div>
            <b>{data.filter((r) => r.needs_review).length} filas requieren revision.</b>{' '}
            Se detectaron datos incompletos o por confirmar. No se inventaron datos.
          </div>
        </div>
      )}

      {/* Section bar + tabla */}
      <div className="venc-secbar">
        <span className="grow" />
        <span className="count">
          {loading ? 'Cargando...' : `${data.length} de ${total} resultados`}
        </span>
      </div>

      {loading && data.length === 0 ? (
        <div className="venc-loading">Cargando vencimientos...</div>
      ) : (
        <FeedTable rows={data} onOpen={openDrawer} onQuickAction={handleQuickAction} busyUid={busyUid} />
      )}

      {/* Paginacion */}
      {pagination && pagination.last_page > 1 && (
        <div
          className="venc-pager"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '14px 0' }}
        >
          <button
            type="button"
            className="venc-btn ghost sm"
            disabled={pagination.current_page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <i className="pi pi-chevron-left" />Anterior
          </button>
          <span style={{ fontSize: 13, color: 'var(--pei-text-helper)' }}>
            Pagina {pagination.current_page} de {pagination.last_page}
          </span>
          <button
            type="button"
            className="venc-btn ghost sm"
            disabled={pagination.current_page >= pagination.last_page}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente<i className="pi pi-chevron-right" />
          </button>
        </div>
      )}

      {/* Drawer detalle cliente (derecha) */}
      <ClienteDrawer
        userId={drawerUser}
        focusUid={focusUid}
        visible={!!drawerUser}
        onClose={closeDrawer}
        toast={toast}
        onMutated={refresh}
      />

      {/* Wizard nuevo cliente externo */}
      {wizardOpen && (
        <NuevoClienteWizard onClose={() => setWizardOpen(false)} toast={toast} onCreated={refresh} />
      )}
    </div>
  );
};

export default VencimientosPage;
